import { formatMoney } from '@theme/money-formatting';
import { prefersReducedMotion } from '@theme/utilities';

/** How long the figures take to count to a new value */
const COUNT_DURATION = 320;

/** The visitor's month counting up from nothing, once per page view */
const ENTRANCE_DURATION = 760;

/** How much of the figure must be on screen before the entrance count starts */
const ENTRANCE_THRESHOLD = 0.6;

/** Waits for the visitor to stop stepping before the screen reader update */
const STATUS_DELAY = 600;

/**
 * Collabs calculator: works out what a creator earns, and what their followers save, from new
 * customers per month and average order. See sections/collabs-calculator.liquid.
 *
 * Liquid renders the figures for the default inputs, so this element only takes over once the
 * visitor changes something.
 */
class CollabsCalculator extends HTMLElement {
  /** @type {AbortController | null} */
  #listeners = null;

  /** Figures as currently shown, in cents, so a count starts from what is on screen */
  #shown = { earn: 0, save: 0 };

  /** @type {Record<string, number>} */
  #frames = {};

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #statusTimer;

  #used = false;

  /** @type {IntersectionObserver | null} */
  #entrance = null;

  /**
   * The last in-range value of each input. While a field is empty or unreadable the figures keep
   * using this instead of dropping to zero, and leaving the field puts it back.
   *
   * @type {WeakMap<HTMLInputElement, number>}
   */
  #lastValid = new WeakMap();

  connectedCallback() {
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    for (const input of this.#inputs) {
      const value = this.#read(input);
      this.#lastValid.set(input, Number.isNaN(value) ? Number(input.min) || 0 : this.#clamp(input, value));
    }

    this.#shown = this.#calculate();

    for (const button of this.querySelectorAll('.collabs-calc__step')) {
      button.addEventListener('click', this.#onStep, { signal });
    }

    for (const input of this.#inputs) {
      input.addEventListener('input', () => this.#update(), { signal });
      // Snap a typed value back into range once the visitor leaves the field
      input.addEventListener('change', () => this.#commit(input), { signal });
    }

    this.#syncButtons();
    this.#prepareEntrance();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#cancelEntrance();
    clearTimeout(this.#statusTimer);
    for (const frame of Object.values(this.#frames)) cancelAnimationFrame(frame);
  }

  /**
   * The page's one authored moment: the earnings figure counts up from zero to the visitor's month,
   * on load when it is already on screen, otherwise the first time it scrolls into view.
   *
   * The stylesheet keeps the figure invisible until this runs (data-counted), so the server-rendered
   * amount never flashes before dropping to zero. Under reduced motion, in the theme editor, or
   * without IntersectionObserver, it is simply revealed as rendered.
   */
  #prepareEntrance() {
    const output = this.querySelector('[data-result="earn"]');
    if (!(output instanceof HTMLElement)) return;

    if (prefersReducedMotion() || window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      output.toggleAttribute('data-counted', true);
      return;
    }

    // Screen readers keep the real amount while the figure shows zero
    output.setAttribute('aria-label', this.#money(this.#shown.earn));
    this.#shown.earn = 0;
    output.textContent = this.#money(0);
    output.toggleAttribute('data-counted', true);

    this.#entrance = new IntersectionObserver(
      ([entry]) => {
        if (!entry || entry.intersectionRatio < ENTRANCE_THRESHOLD) return;
        this.#cancelEntrance();
        this.#animate('earn', this.#calculate().earn, ENTRANCE_DURATION);
      },
      { threshold: ENTRANCE_THRESHOLD }
    );
    this.#entrance.observe(output);
  }

  /** Stops waiting for the entrance; the figure then counts or sits like any other change */
  #cancelEntrance() {
    this.#entrance?.disconnect();
    this.#entrance = null;
    this.querySelector('[data-result="earn"]')?.removeAttribute('aria-label');
  }

  /** @returns {HTMLInputElement[]} */
  get #inputs() {
    return [...this.querySelectorAll('input[data-input]')];
  }

  /**
   * @param {MouseEvent} event
   */
  #onStep = (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const input = /** @type {HTMLInputElement | null} */ (
      document.getElementById(button.getAttribute('aria-controls') ?? '')
    );
    if (!input) return;

    const step = Number(input.step) || 1;
    const direction = Number(button.dataset.direction) || 1;
    // Steps land on the grid (55 -> 60, not 65) so values stay round after a typed number
    const current = this.#value(input);
    const next =
      direction > 0 ? Math.floor(current / step) * step + step : Math.ceil(current / step) * step - step;

    input.value = String(this.#clamp(input, next));
    this.#update();
  };

  /**
   * @param {HTMLInputElement} input
   */
  #commit(input) {
    input.value = String(this.#value(input));
    this.#update();
  }

  #update() {
    // A change before the figure scrolled into view counts up from zero as part of this update instead
    this.#cancelEntrance();

    for (const input of this.#inputs) this.#validate(input);
    this.#syncButtons();

    // Keeps the order input as wide as its digits (see .collabs-calc__money)
    for (const input of this.#inputs) {
      input.style.setProperty('--collabs-calc-digits', String(Math.max(1, input.value.length)));
    }

    const next = this.#calculate();
    this.#animate('earn', next.earn);
    this.#animate('save', next.save);
    this.#announce(next);

    if (!this.#used) {
      this.#used = true;
      this.#track('collabs_calculator_use');
    }
  }

  /**
   * @returns {{ earn: number, save: number }} Both in cents
   */
  #calculate() {
    const [customers, order] = ['customers', 'order'].map((name) => {
      const input = this.querySelector(`input[data-input="${name}"]`);
      return input instanceof HTMLInputElement ? this.#value(input) : 0;
    });

    const commission = Number(this.dataset.commission) || 0;
    const discount = Number(this.dataset.discount) || 0;

    const spend = customers * order * 100;
    const save = (spend * discount) / 100;
    const base = this.hasAttribute('data-after-discount') ? spend - save : spend;

    // Whole currency units, matching the Liquid render: these are example figures, not quotes
    return { earn: toWholeUnits((base * commission) / 100), save: toWholeUnits(save) };
  }

  /**
   * Shows or hides the input's range message and remembers it when it holds a usable value.
   *
   * @param {HTMLInputElement} input
   */
  #validate(input) {
    const value = this.#read(input);
    const inRange = !Number.isNaN(value) && value === this.#clamp(input, value);

    if (!Number.isNaN(value)) this.#lastValid.set(input, this.#clamp(input, value));

    if (inRange) {
      input.removeAttribute('aria-invalid');
    } else {
      input.setAttribute('aria-invalid', 'true');
    }

    const error = this.querySelector(`#${CSS.escape(input.getAttribute('aria-describedby') ?? '')}`);
    if (error instanceof HTMLElement) error.hidden = inRange;
  }

  /**
   * Counts a figure from what is on screen to its new value.
   *
   * @param {'earn' | 'save'} key
   * @param {number} target Cents
   * @param {number} [duration]
   */
  #animate(key, target, duration = COUNT_DURATION) {
    const output = this.querySelector(`[data-result="${key}"]`);
    if (!output) return;

    cancelAnimationFrame(this.#frames[key] ?? 0);
    const from = this.#shown[key];

    if (from === target || prefersReducedMotion()) {
      this.#shown[key] = target;
      output.textContent = this.#money(target);
      return;
    }

    const start = performance.now();
    const tick = (/** @type {number} */ now) => {
      // A frame's timestamp can predate the start time slightly; clamp so the count never runs backwards
      const progress = Math.min(1, Math.max(0, (now - start) / duration));
      // Ease out expo: the count races through the small numbers and settles on the real one
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      const value = progress === 1 ? target : toWholeUnits(from + (target - from) * eased);

      this.#shown[key] = value;
      output.textContent = this.#money(value);

      if (progress < 1) this.#frames[key] = requestAnimationFrame(tick);
    };

    this.#frames[key] = requestAnimationFrame(tick);
  }

  /**
   * @param {{ earn: number, save: number }} figures
   */
  #announce({ earn, save }) {
    const status = this.querySelector('[data-status]');
    if (!(status instanceof HTMLElement)) return;

    clearTimeout(this.#statusTimer);
    this.#statusTimer = setTimeout(() => {
      const template = status.dataset.template || '[earn] / [save]';
      status.textContent = template.replaceAll('[earn]', this.#money(earn)).replaceAll('[save]', this.#money(save));
    }, STATUS_DELAY);
  }

  #syncButtons() {
    for (const button of this.querySelectorAll('.collabs-calc__step')) {
      if (!(button instanceof HTMLButtonElement)) continue;

      const input = document.getElementById(button.getAttribute('aria-controls') ?? '');
      if (!(input instanceof HTMLInputElement)) continue;

      const value = this.#value(input);
      button.disabled = Number(button.dataset.direction) > 0 ? value >= Number(input.max) : value <= Number(input.min);
    }
  }

  /**
   * @param {number} cents
   */
  #money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'GBP';
    // Matches Liquid's money_without_trailing_zeros, so £27.00 reads £27 before and after JS
    return formatMoney(cents, format, currency).replace(/[.,]00(?=\D*$)/, '');
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {number} The typed number rounded to a whole number, or NaN when the field is empty or
   *   unreadable (number inputs report letters and stray symbols as an empty value)
   */
  #read(input) {
    return Math.round(input.valueAsNumber);
  }

  /**
   * The number the calculator uses for an input: what was typed, held within its limits, or the last
   * usable value while the field is empty.
   *
   * @param {HTMLInputElement} input
   */
  #value(input) {
    const value = this.#read(input);
    return Number.isNaN(value) ? this.#lastValid.get(input) ?? (Number(input.min) || 0) : this.#clamp(input, value);
  }

  /**
   * @param {HTMLInputElement} input
   * @param {number} value
   */
  #clamp(input, value) {
    const max = input.max === '' ? Infinity : Number(input.max);
    return Math.min(max, Math.max(Number(input.min) || 0, value));
  }

  /**
   * Sends a GA4 event through the theme's analytics helper (assets/analytics.js), which no-ops if
   * gtag hasn't loaded.
   *
   * @param {string} event
   * @param {Record<string, unknown>} [params]
   */
  #track(event, params = {}) {
    trackEvent(event, { section_id: this.dataset.sectionId, ...params });
  }
}

/**
 * @param {number} cents
 */
function toWholeUnits(cents) {
  return Math.round(cents / 100) * 100;
}

/**
 * @param {string} event
 * @param {Record<string, unknown>} params
 */
function trackEvent(event, params) {
  if (window.Shopify?.designMode) return;

  try {
    window.track?.(event, params);
  } catch {
    // Analytics must never break the interaction
  }
}

if (!customElements.get('collabs-calculator')) {
  customElements.define('collabs-calculator', CollabsCalculator);

  // Every Apply link on the page leaves for Shopify Collabs, including the theme's own button blocks,
  // so they are tracked here in one place. `location` says which one was used.
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href*="collabs.shopify.com"]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    const section = link.closest('.shopify-section');
    trackEvent('collabs_apply_click', {
      location: link.dataset.collabsApply || section?.id.replace('shopify-section-', '') || 'page',
    });
  });
}
